import React, { useEffect, useState, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

/* ---- SAFETY HOOK (prevents crash when outside MapContainer) ---- */
function useSafeMap() {
  try {
    return useMap();
  } catch {
    return null;
  }
}

/* SingleVectorLayer: handles one layer instance (add/remove & style) */
const SingleVectorLayer = ({ layer }) => {
  const map = useSafeMap();
  const ref = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Remove previous instance if any
    if (ref.current) {
      try { map.removeLayer(ref.current); } catch (_) {}
      ref.current = null;
    }

    if (!layer || !layer.data || !layer.visible) return;

    try {
      const g = L.geoJSON(layer.data, {
        pane: layer.pane || undefined,
        style: (feature) => ({
          color: layer.color,
          weight: 3,
          opacity: Math.max(0, Math.min(1, layer.opacity ?? 1)),
          fillColor: layer.color,
          fillOpacity: Math.max(0, Math.min(1, (layer.opacity ?? 1) * 0.3))
        }),
        pointToLayer: (feature, latlng) =>
          L.circleMarker(latlng, {
            radius: 6,
            fillColor: layer.color,
            color: "#fff",
            weight: 1,
            fillOpacity: 0.9
          }),
        /* ---- UPDATED POPUP LOGIC HERE ---- */
        onEachFeature: (feature, l) => {
          try {
            const props = feature.properties || {};
            
            // Start building popup HTML
            let content = `<div style="min-width: 200px; font-family: sans-serif; font-size: 12px;">`;
            
            // Title Header
            content += `<h3 style="margin: 0 0 8px; font-size: 14px; font-weight: bold; color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px;">
              ${layer.title || "Feature Info"}
            </h3>`;

            // Property Table
            content += `<table style="width: 100%; border-collapse: collapse;">`;
            
            // Loop through all properties in the GeoJSON
            Object.entries(props).forEach(([key, value]) => {
              // Optional: Skip internal AutoCAD properties if you want (e.g., EntityHand)
              // if (key === "EntityHand" || key === "PaperSpace") return;

              content += `
                <tr style="border-bottom: 1px solid #f0f0f0;">
                  <td style="padding: 3px 5px; font-weight: 600; color: #555; vertical-align: top; width: 40%;">${key}</td>
                  <td style="padding: 3px 5px; color: #000; word-wrap: break-word;">${value}</td>
                </tr>
              `;
            });
            
            content += `</table></div>`;

            l.bindPopup(content);
          } catch (err) {
            console.warn("Error binding popup:", err);
          }
        }
      });

      g.addTo(map);
      ref.current = g;

      if (layer.isFirstLayer) {
        try {
          const bounds = g.getBounds();
          if (bounds && (typeof bounds.isValid === "function" ? bounds.isValid() : !bounds.isEmpty())) {
            map.fitBounds(bounds, { padding: [40, 40] });
          }
        } catch (e) { /* ignore fit errors */ }
      }
    } catch (err) {
      console.warn("[SingleVectorLayer] render failed", err);
    }

    return () => {
      if (map && ref.current) {
        try { map.removeLayer(ref.current); } catch (_) {}
        ref.current = null;
      }
    };
  }, [
    map,
    layer?.data,
    layer?.visible,
    layer?.opacity,
    layer?.color,
    layer?.title,
    layer?.isFirstLayer,
    layer?.pane
  ]);

  return null;
};

/* VectorLeaflet: discovery, fetch, merge, listeners */
const VectorLeaflet = ({ latestSurvey, apiBase, pane = "overlayPane" }) => {
  const [layers, setLayers] = useState([]); // {id,name,title,url,color,visible,opacity,data,isFirstLayer,status,pane}
  const loadingRef = useRef({});            // locks per id while fetching
  const isMountedRef = useRef(false);

  const toDateKey = (raw) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
    return null;
  };

  // Color palette - expanded for more unique colors
  const COLOR_PALETTE = [
    "#00FFFF", "#FFFF00", "#FF00FF", "#00FF00", "#FFA500", 
    "#FF69B4", "#008080", "#FF7F50", "#4169E1", "#DC143C",
    "#32CD32", "#FF1493", "#00CED1", "#FFD700", "#8A2BE2",
    "#FF6347", "#20B2AA", "#FF4500", "#9370DB", "#3CB371",
    "#FF8C00", "#BA55D3", "#48D1CC", "#FF69B4", "#00FA9A"
  ];

  const getUniqueColor = (usedColors) => {
    // Filter out colors that are already in use
    const availableColors = COLOR_PALETTE.filter(c => !usedColors.includes(c));
    
    // If all colors are used, cycle through the palette
    if (availableColors.length === 0) {
      // Find the least recently used color by checking which color appears least in usedColors
      const colorCounts = COLOR_PALETTE.map(color => ({
        color,
        count: usedColors.filter(c => c === color).length
      }));
      colorCounts.sort((a, b) => a.count - b.count);
      return colorCounts[0].color;
    }
    
    // Return a random color from available colors
    return availableColors[Math.floor(Math.random() * availableColors.length)];
  };

  // DISCOVERY: Merge discovered layers with existing state (preserve visible/opacity/data/color/isFirstLayer)
  useEffect(() => {
    if (!latestSurvey || !apiBase) {
      setLayers([]);
      return;
    }
    isMountedRef.current = true;
    loadingRef.current = {};

    const discoverLayers = async () => {
      try {
        const p1 = String(latestSurvey.location_name || "").trim();
        const p2 = toDateKey(latestSurvey.survey_time);
        if (!p1 || !p2) {
          setLayers([]);
          window.dispatchEvent(new CustomEvent("vector-layers-discovered", { detail: { location: p1, survey_time: p2, full_layers: [] } }));
          return;
        }

        const params = new URLSearchParams({ location_name: p1, survey_time: p2 });
        const cleanBase = String(apiBase).replace(/\/+$/, "");
        const discoveryUrl = `${cleanBase}/geojson/discovery?${params.toString()}`;

        const r = await fetch(discoveryUrl);
        if (!r.ok) {
          console.warn("[VectorLeaflet] discovery failed", r.status);
          setLayers([]);
          window.dispatchEvent(new CustomEvent("vector-layers-discovered", { detail: { location: p1, survey_time: p2, full_layers: [] } }));
          return;
        }

        const json = await r.json();
        const candidates = Array.isArray(json.found) ? json.found : [];

        if (candidates.length === 0) {
          setLayers((prev) => {
            // Keep manual (non auto-discovered) layers
            const manual = prev.filter(p => !String(p.id).startsWith("vector-"));
            window.dispatchEvent(new CustomEvent("vector-layers-discovered", { detail: { location: p1, survey_time: p2, full_layers: [] } }));
            return manual;
          });
          return;
        }

        // Merge with existing layers to preserve state (visible/opacity/data/color/isFirstLayer)
        setLayers(prev => {
          const prevById = prev.reduce((acc, l) => { acc[l.id] = l; return acc; }, {});
          
          // Collect colors already in use by existing layers
          const usedColors = Object.values(prevById)
            .map(l => l.color)
            .filter(c => c && COLOR_PALETTE.includes(c));
          
          // Create discovered layers with unique colors
          const discovered = candidates.map((cand, idx) => {
            const folderName = String(cand.layer || "unknown");
            let fileName = String(cand.itemname || `layer-${idx}`);
            if (fileName.toLowerCase().endsWith(".geojson")) fileName = fileName.slice(0, -8);
            const id = `vector-${folderName}-${fileName}`;
            // Build URL defensively by encoding segments
            const url = `${cleanBase}/geojson/${encodeURIComponent(p1)}/${encodeURIComponent(p2)}/${encodeURIComponent(folderName)}/${encodeURIComponent(fileName)}.geojson`;
            
            const existing = prevById[id];
            // If layer exists, keep its color; otherwise assign a unique new color
            const layerColor = existing?.color || getUniqueColor(usedColors);
            if (!existing) {
              usedColors.push(layerColor); // Track this color as used
            }
            
            return {
              id,
              name: folderName,
              title: fileName,
              url,
              color: layerColor,
              visible: true,
              opacity: 1.0,
              data: null,
              isFirstLayer: idx === 0,
              status: "discovered",
              pane
            };
          });
          
          const merged = discovered.map(d => {
            const existing = prevById[d.id];
            if (!existing) return d;
            return {
              ...d,
              visible: typeof existing.visible === "boolean" ? existing.visible : d.visible,
              opacity: typeof existing.opacity === "number" ? existing.opacity : d.opacity,
              color: existing.color || d.color, // Preserve existing color
              data: existing.data || d.data,
              // preserve isFirstLayer if previously set
              isFirstLayer: typeof existing.isFirstLayer === "boolean" ? existing.isFirstLayer : d.isFirstLayer,
              status: existing.status === "ok" ? "ok" : d.status,
              pane: existing.pane || d.pane
            };
          });

          // Keep any non-auto layers (manual or user vectors) that are not auto-discovered
          const manual = prev.filter(p => !String(p.id).startsWith("vector-") && !merged.find(m => m.id === p.id));
          const result = [...merged, ...manual];

          // Emit discovered event with merged auto layers (not including manual)
          window.dispatchEvent(new CustomEvent("vector-layers-discovered", { detail: { location: p1, survey_time: p2, full_layers: merged } }));

          return result;
        });

      } catch (err) {
        console.warn("[VectorLeaflet] discovery error", err);
        setLayers([]);
      }
    };

    discoverLayers();

    return () => { isMountedRef.current = false; loadingRef.current = {}; };
  }, [latestSurvey, apiBase, pane]);

  // FETCH: only load missing data and respect loadingRef locks
  useEffect(() => {
    const toLoad = layers.filter(l => l.url && !l.data && !loadingRef.current[l.id]);
    if (toLoad.length === 0) return;

    toLoad.forEach(layer => {
      loadingRef.current[layer.id] = true;

      fetch(layer.url, { headers: { Accept: "application/geo+json, application/json" } })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(geojson => {
          if (!isMountedRef.current) return;
          setLayers(prev => prev.map(p => p.id === layer.id ? { ...p, data: geojson, status: "ok" } : p));
        })
        .catch(err => {
          console.warn(`[VectorLeaflet] failed to load ${layer.id}`, err);
          setLayers(prev => prev.map(p => p.id === layer.id ? { ...p, status: "error", statusMsg: String(err) } : p));
        })
        .finally(() => {
          delete loadingRef.current[layer.id];
        });
    });
  }, [layers]);

  // EVENT LISTENERS: toggle, opacity, add, remove (match Cesium behaviour)
  useEffect(() => {
    const handleToggle = (ev) => {
      const { id, visible } = ev.detail || {};
      if (!id) return;
      setLayers(prev => prev.map(l => (l.id === id || l.name === id) ? { ...l, visible: !!visible } : l));
    };

    const handleOpacity = (ev) => {
      const { id, opacity } = ev.detail || {};
      if (!id) return;
      const op = Number(opacity);
      setLayers(prev => prev.map(l => (l.id === id || l.name === id) ? { ...l, opacity: Number.isFinite(op) ? op : l.opacity } : l));
    };

    const handleAdd = (ev) => {
      const detail = ev.detail || {};
      if (!detail || !detail.id) return;
      
      setLayers(prev => {
        // Check if layer already exists
        if (prev.find(p => p.id === detail.id)) return prev;
        
        // Collect colors already in use
        const usedColors = prev
          .map(l => l.color)
          .filter(c => c && COLOR_PALETTE.includes(c));
        
        // Assign unique color if not provided
        const layerColor = detail.color || getUniqueColor(usedColors);
        
        const incoming = {
          id: detail.id,
          name: detail.name || detail.id,
          title: detail.title || detail.id,
          url: detail.url || null,
          color: layerColor,
          visible: typeof detail.visible === "boolean" ? detail.visible : true,
          opacity: typeof detail.opacity === "number" ? detail.opacity : 1.0,
          data: detail.data || null,
          isFirstLayer: !!detail.isFirstLayer,
          status: detail.data ? "ok" : "added",
          pane: detail.pane || pane
        };
        
        const next = [incoming, ...prev];
        window.dispatchEvent(new CustomEvent("vector-layers-updated", { detail: { layers: next } }));
        return next;
      });
    };

    const handleRemove = (ev) => {
      const { id } = ev.detail || {};
      if (!id) return;
      setLayers(prev => prev.filter(l => !(l.id === id || l.name === id)));
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    window.addEventListener("map-set-opacity", handleOpacity);
    window.addEventListener("map-add-layer", handleAdd);
    window.addEventListener("map-remove-layer", handleRemove);

    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
      window.removeEventListener("map-set-opacity", handleOpacity);
      window.removeEventListener("map-add-layer", handleAdd);
      window.removeEventListener("map-remove-layer", handleRemove);
    };
  }, [pane]);

  // Keep other parts of the app informed
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("vector-layers-updated", { detail: { layers } }));
  }, [layers]);

  return (
    <>
      {layers.map(l => (
        <SingleVectorLayer key={l.id} layer={l} />
      ))}
    </>
  );
};

export default VectorLeaflet;
